import _ from 'lodash';
import { ERROR_MESSAGES, ERROR_TYPES } from './errorMessages';

/**
 * Centralized error handler for onboarding module
 * Follows existing codebase patterns (lodash error checking, MessageBox display)
 */
export class OnboardingErrorHandler {
  /**
   * Process API error and return structured error info
   * @param {Error} error - The error object from API call
   * @param {string} context - Context of the operation (e.g., 'registration', 'email_verification')
   * @returns {Object} Structured error information
   */
  static processError(error, context = 'general') {
    const errorInfo = {
      type: ERROR_TYPES.UNKNOWN,
      message: ERROR_MESSAGES.GENERIC_ERROR,
      originalError: error,
      context,
      shouldLog: true
    };

    // Network error handling (follows existing auth pattern)
    if (error.message === "Network Error") {
      errorInfo.type = ERROR_TYPES.NETWORK;
      errorInfo.message = ERROR_MESSAGES.NETWORK_ERROR;
      console.error(`Network error in ${context}:`, error);
      return errorInfo;
    }

    // API error handling (follows existing lodash pattern)
    if (_.has(error, 'response.data')) {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          errorInfo.type = ERROR_TYPES.VALIDATION;
          errorInfo.message = this._getValidationError(data, context);
          break;
          
        case 401:
          errorInfo.type = ERROR_TYPES.AUTH;
          errorInfo.message = ERROR_MESSAGES.AUTH_ERROR;
          break;
          
        case 403:
          errorInfo.type = ERROR_TYPES.AUTH;
          errorInfo.message = ERROR_MESSAGES.ACCESS_DENIED;
          break;
          
        case 409:
          errorInfo.type = ERROR_TYPES.CONFLICT;
          errorInfo.message = this._getConflictError(data, context);
          break;
          
        case 500:
          errorInfo.type = ERROR_TYPES.SERVER;
          errorInfo.message = ERROR_MESSAGES.SERVER_ERROR;
          break;
          
        default:
          errorInfo.message = data.message || ERROR_MESSAGES.GENERIC_ERROR;
      }
      
      console.error(`API error ${status} in ${context}:`, error.response);
    } else {
      // Client-side error
      console.error(`Client error in ${context}:`, error);
    }

    return errorInfo;
  }

  /**
   * Handle validation errors based on context
   * @private
   */
  static _getValidationError(data, context) {
    // Check for specific validation error patterns in API message
    if (data.message) {
      const message = data.message.toLowerCase();

      // Check for organization/company address specific errors
      if (message.includes('adresse') || message.includes('address')) {
        return ERROR_MESSAGES.VALIDATION_COMPANY_ADDRESS;
      }

      // Check for organization already in registration (privacy-safe message)
      if (message.includes('allerede under registrering')) {
        return ERROR_MESSAGES.ORG_NUMBER_IN_REGISTRATION;
      }

      // Check for organization/company info errors
      if (message.includes('organisasjon') || message.includes('bedrift') ||
          message.includes('company') || message.includes('org')) {
        return ERROR_MESSAGES.VALIDATION_COMPANY_INFO_INCOMPLETE;
      }

      // Check for generic registration error from server
      if (context === 'registration' && message.includes('opprettelse av bruker')) {
        return ERROR_MESSAGES.VALIDATION_REGISTRATION;
      }

      // Return original API message if it doesn't match specific patterns
      return data.message;
    }

    switch (context) {
      case 'registration':
        return ERROR_MESSAGES.VALIDATION_REGISTRATION;
      case 'email_verification':
        return ERROR_MESSAGES.VALIDATION_EMAIL;
      case 'company_info':
        return ERROR_MESSAGES.VALIDATION_COMPANY;
      default:
        return ERROR_MESSAGES.VALIDATION_GENERAL;
    }
  }

  /**
   * Handle conflict errors based on context and message content
   * @private
   */
  static _getConflictError(data, context) {
    const message = data.message || '';
    
    // Follow existing pattern from OnboardingContainer.js
    if (message.includes("e-postadressen")) {
      return ERROR_MESSAGES.USER_EXISTS;
    }
    
    if (message.includes("organisasjonsnummer")) {
      return ERROR_MESSAGES.ORG_NUMBER_EXISTS;
    }
    
    return data.message || ERROR_MESSAGES.CONFLICT_GENERAL;
  }


  /**
   * Combined error processing 
   * @param {Error} error - The error object
   * @param {string} context - Context of the operation
   * @returns {Object} Processed error information
   */
  static handleError(error, context = 'general') {
    const errorInfo = this.processError(error, context);
    return errorInfo;
  }

  /**
   * Create error display object for MessageBox component
   * @param {Object} errorInfo - Error information from processError
   * @returns {Object} Error display configuration
   */
  static getErrorDisplay(errorInfo) {
    return {
      message: errorInfo.message,
      variant: this._getErrorVariant(errorInfo.type),
      type: errorInfo.type
    };
  }

  /**
   * Get MessageBox variant based on error type
   * @private
   */
  static _getErrorVariant(errorType) {
    switch (errorType) {
      case ERROR_TYPES.VALIDATION:
        return 'warning';
      case ERROR_TYPES.NETWORK:
      case ERROR_TYPES.SERVER:
        return 'error';
      default:
        return 'error';
    }
  }
}

// Convenience functions for common use cases
export const handleApiError = (error, context) => 
  OnboardingErrorHandler.handleError(error, context);

export const processError = (error, context) => 
  OnboardingErrorHandler.processError(error, context);

export const getErrorDisplay = (errorInfo) => 
  OnboardingErrorHandler.getErrorDisplay(errorInfo);